import {
  ArrowDownTrayIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import date from "date-and-time";
import { IInvoice } from "../../models/invoice/IInvoice";
import LoadingWheel from "../shared/LoadingWheel";
import useInvoices from "../../data/useInvoices";
import { useEffect, useMemo, useState } from "react";
import InvoiceModal from "../invoices/InvoiceModal";
import { useForm } from "react-hook-form";
import SelectInput from "../shared/Inputs/SelectInput";
import { InvoiceStatus } from "../../models/Invoice";
import { DateTimeInput } from "../shared/Inputs/DateTimeInput";
import { SearchAccount } from "../accounts/SearchAccount";
import PagedTable, { DateRangeType } from "../shared/Tables/PagedTable";
import { createColumnHelper, RowModel, Table } from "@tanstack/react-table";
import IndeterminateCheckbox from "../shared/Tables/IndeterminateCheckbox";
import TableCheckboxCol from "../shared/Tables/IndeterminateCheckbox";
import useApiHelper from "../../hooks/useApiHelper";
import apiConfig from "../../utils/apiConfig";
import useInvoiceMutations from "../../data/useInvoiceMutations";

interface InvoiceRowProps {
  invoice: IInvoice;
  onClick: (e: React.MouseEvent<HTMLTableRowElement, MouseEvent>) => void;
}

interface InvoiceListProps {
  status?: string;
  accountId?: string;
  partId?: string;
  invoiceDate?: DateRangeType;
  hideFilters?: boolean;
}

export default function InvoiceList(filters: InvoiceListProps) {
  const { handleSubmit, register, setValue, watch } = useForm({
    defaultValues: {
      status: filters.status ?? undefined,
      accountId: filters.accountId ?? undefined,
      partId: filters.partId ?? undefined,
      invoiceDate: filters.invoiceDate ?? { fieldName: "invoiceDate", start: null, end: null },
    }
  });
  const { post } = useApiHelper(apiConfig("InvoiceList"));

  const status = watch("status");
  const accountId = watch("accountId");
  const partId = watch("partId");
  const start = watch("invoiceDate.start");
  const end = watch("invoiceDate.end");
  const dateRange = watch("invoiceDate") as DateRangeType;

  const [downloadingIds, setDownloadingIds] = useState<string[]>([]);


  const trueFilters = useMemo(() => ({
    status: filters.status ?? status,
    accountId: filters.accountId ?? accountId,
    partId: filters.partId ?? partId,
    invoiceDate: filters.invoiceDate ?? (start && end ? dateRange : undefined),
  }), [filters, status, accountId, partId, start, end, dateRange]);


  const { downloadInvoice, downloadBulkInvoices } = useInvoiceMutations();


  const [openModal, setOpenModal] = useState(false);
  const [invoice, setInvoice] = useState<IInvoice | null>(null);

  const colHelper = createColumnHelper<IInvoice>();

  const cols = useMemo(
    () => [
      TableCheckboxCol(false),
      colHelper.accessor("invoiceNumber", {
        header: "Invoice No",
        sortingFn: "auto",
      }),
      colHelper.accessor("account.name", {
        header: "Account Name",
        sortingFn: "auto",
      }),
      colHelper.accessor("invoiceDate", {
        header: "Invoice Date",
        sortingFn: "auto",
        cell: (info) => <span>{info.getValue() !== null ? date.format(new Date(info.getValue()!), "DD/MM/YYYY") : "Not Set"}</span>,
      }),
      colHelper.accessor("reference", {
        header: "Reference",
        sortingFn: "auto",
      }),
      colHelper.accessor("dueDate", {
        header: "Due Date",
        sortingFn: "auto",
        cell: (info) => <span>{info.getValue() !== null ? date.format(new Date(info.getValue()!), "DD/MM/YYYY") : "Not Set"}</span>,
      }),
      colHelper.accessor("invoiceTotal", {
        header: "Total",
        sortingFn: "auto",
        cell: (info) => <span>£{info.getValue() !== null ? info.getValue() : "Not Set"}</span>,
      }),
      colHelper.accessor("status", {
        header: "Status",
        sortingFn: "auto",
        cell: (info) => <span
          className={`px-3 py-1 rounded-full font-bold ${{
            0: "bg-blue-100 text-blue-600",
            1: "bg-amber-100 text-amber-600",
            2: "bg-green-100 text-green-600",
            3: "bg-red-100 text-red-600",
          }[info.getValue()]
            }`}
        >
          {
            {
              0: "Draft",
              1: "Approved",
              2: "Paid",
              3: "Credit",
            }[info.getValue()]
          }
        </span>,
      }),
      colHelper.display({
        id: "actions",
        header: "Actions",
        cell: (info) => {
          let downloading = false;

          return (
            <div className="px-3 flex justify-center space-x-4">
              {downloadingIds.includes(info.row.original.id) && <div className="max-w-5 max-h-5 flex items-center justify-center"><LoadingWheel /></div>}
              <ArrowDownTrayIcon
                className={`w-5 h-5 text-gray-600 hover:text-primary transition-colors duration-200 ${downloadingIds.includes(info.row.original.id) ? "hidden" : ""}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setDownloadingIds((prev) => [...prev, info.row.original.id]);
                  downloadInvoice(info.row.original.id).then(() => {
                    setDownloadingIds((prev) => prev.filter((id) => id !== info.row.original.id));
                  });
                }}
              />
            </div>
          );
        }
      }),
    ],
    []
  );

  const onSubmit = (data: any) => {
    console.log(data);
  };

  useEffect(() => {
    if (openModal) return;
    setInvoice(null);

  }, [openModal])

  // var table = undefined;

  // if (invoices.isLoading) {
  //   table = (
  //     <div className="flex items-center justify-center py-10">
  //       <LoadingWheel />
  //     </div>
  //   );
  // } else if (
  //   invoices.isError ||
  //   !invoices.data ||
  //   !invoices.data.data
  // ) {
  //   table = (
  //     <div className="flex items-center justify-center py-10">
  //       <p className="text-red-600 font-bold text-xl">Error loading invoices</p>
  //     </div>
  //   );
  // } else {
  //   table = (

  //   );
  // }


  return <>
    {/* ---------- Filter ---------- */}
    <form onSubmit={handleSubmit(onSubmit)} className="mt-2 mx-4 grid grid-cols-3 gap-4">
      {filters.accountId == undefined && (
        <div className="col-span-3">
          <SearchAccount
            label="Account"
            onAccountSelect={(account) => setValue("accountId", account?.id)}
          />
        </div>
      )}

      {filters.status == undefined && (
        <SelectInput
          label="Status"
          options={[
            { id: undefined, name: "All" },
            { id: InvoiceStatus.Draft, name: "Draft" },
            { id: InvoiceStatus.Approved, name: "Approved" },
            { id: InvoiceStatus.Paid, name: "Paid" },
            { id: InvoiceStatus.Credit, name: "Credit" },
          ]}
          register={register}
          registerName="status"
          setValue={setValue}
          watch={watch}
        />
      )}
      {filters.invoiceDate?.start == undefined && (
        <DateTimeInput
          label="From"
          register={register}
          registerName="invoiceDate.start"
        />
      )}
      {filters.invoiceDate?.end == undefined && (
        <DateTimeInput
          label="To"
          register={register}
          registerName="invoiceDate.end"
        />
      )}

    </form>
    <hr className="text-gray-300 mt-8 col-span-3 mx-4" />
    {/* ---------------------------- */}
    <div className="p-4">
      <InvoiceModal
        open={openModal}
        setOpen={setOpenModal}
        invoice={invoice}
      />

      <div className="sm:hidden p-2">
        <p className="text-center">Tap a row to see the full record</p>
      </div>

      <PagedTable
        url="/invoice/filtered"
        columns={cols}
        queryLabel="Search part details"
        onRowClick={(x: IInvoice) => {
          setInvoice(x);
          setOpenModal(true);
        }}
        showTotal={true}
        additionalBody={trueFilters}
        actionButtons={[
          {
            label: "Download Xero",
            onClick: (rows: RowModel<IInvoice>, table: Table<IInvoice>) => {
              downloadBulkInvoices(rows.rows.map((row: any) => row.original.id), "xero");
            },
            disabledOnNoRows: true,
          }
        ]}
      />
    </div>
  </>
}
